import { useCallback, useEffect, useRef, useState, type MouseEvent } from 'react';
import { toast } from 'react-toastify';
import { updatePromptWithMedia } from './promptsV2Api';
import type { AppPrompt, PromptVisibility } from './promptsV2Types';
import { PromptVideoUploadBar } from './PromptVideoUploadBar';
import type { PromptVideoUploadPhase } from './promptVideoUploadTracker';
import { docMetaFromUrl, hasPromptVideo, mediaUrl, overlayClose } from './promptsV2Utils';
import { PromptVideoRecordingModal } from './PromptVideoRecordingModal';
import { VisibilitySelectDropdown } from './VisibilitySelectDropdown';
import './PromptEditModal.css';

export type PromptEditModalProps = {
  prompt: AppPrompt | null;
  onClose: () => void;
  onSaved: (updated: AppPrompt) => void;
  onDeleteRequest?: (promptId: number) => void;
  showDelete?: boolean;
};

export function PromptEditModal({
  prompt,
  onClose,
  onSaved,
  onDeleteRequest,
  showDelete = true,
}: PromptEditModalProps) {
  const uploadInputRef = useRef<HTMLInputElement | null>(null);
  const docInputRef = useRef<HTMLInputElement | null>(null);
  const [description, setDescription] = useState('');
  const [visibility, setVisibility] = useState<PromptVisibility>('public');
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [videoPreviewUrl, setVideoPreviewUrl] = useState<string | null>(null);
  const [docFile, setDocFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  const [videoUpload, setVideoUpload] = useState<{
    progress: number;
    phase: PromptVideoUploadPhase;
  } | null>(null);
  const [recordingOpen, setRecordingOpen] = useState(false);

  const videoPreviewUrlRef = useRef<string | null>(null);
  videoPreviewUrlRef.current = videoPreviewUrl;

  const setVideoSelection = useCallback((file: File | null) => {
    if (videoPreviewUrlRef.current) {
      URL.revokeObjectURL(videoPreviewUrlRef.current);
    }
    setVideoFile(file);
    setVideoPreviewUrl(file ? URL.createObjectURL(file) : null);
  }, []);

  useEffect(() => {
    if (!prompt) return;
    setDescription(prompt.description ?? '');
    setVisibility(prompt.visibility ?? 'public');
    setVideoSelection(null);
    setDocFile(null);
    setVideoUpload(null);
  }, [prompt, setVideoSelection]);

  useEffect(() => {
    if (!prompt) {
      setRecordingOpen(false);
    }
  }, [prompt]);

  useEffect(
    () => () => {
      if (videoPreviewUrl) URL.revokeObjectURL(videoPreviewUrl);
    },
    [videoPreviewUrl]
  );

  function handleClose() {
    if (saving) return;
    setVideoSelection(null);
    setDocFile(null);
    onClose();
  }

  async function handleSave() {
    if (!prompt) return;
    setSaving(true);
    if (videoFile) {
      setVideoUpload({ progress: 0, phase: 'presigning' });
    }
    try {
      const updated = await updatePromptWithMedia(prompt.id, {
        content: prompt.content,
        description,
        visibility,
        videoFile,
        docFile,
        onVideoProgress: (progress) =>
          setVideoUpload({
            progress: progress.percent,
            phase: progress.phase,
          }),
      });
      setVideoUpload(null);
      onSaved(updated);
      handleClose();
      toast.success('Prompt updated.');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to update prompt');
      setVideoUpload(null);
    } finally {
      setSaving(false);
    }
  }

  if (!prompt) return null;

  const docUrl = prompt.supportedDocumentUrl?.trim();
  const docMeta = docUrl ? docMetaFromUrl(docUrl) : null;
  const existingVideoUrl = hasPromptVideo(prompt) ? mediaUrl(prompt.fileUrl ?? undefined) : null;
  const activeVideoSrc = videoPreviewUrl ?? existingVideoUrl;
  const showVideo = Boolean(activeVideoSrc);
  const uploadBusy = saving && Boolean(videoUpload);

  return (
    <div className="prompt-edit-modal">
      <div
        className={`prompt-edit-modal__backdrop${
          recordingOpen ? ' prompt-edit-modal__backdrop--behind-studio' : ''
        }`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="prompt-edit-modal-title"
        onMouseDown={(e: MouseEvent<HTMLDivElement>) => overlayClose(e, handleClose)}
      >
        <div className="prompt-edit-modal__dialog" onMouseDown={(e) => e.stopPropagation()}>
          <header className="prompt-edit-modal__header">
            <div>
              <h2 id="prompt-edit-modal-title" className="prompt-edit-modal__title">
                Edit prompt response
              </h2>
              <p className="prompt-edit-modal__question">{prompt.content}</p>
            </div>
            <button
              type="button"
              className="prompt-edit-modal__close"
              aria-label="Close"
              disabled={saving}
              onClick={handleClose}
            >
              ×
            </button>
          </header>

          <div className="prompt-edit-modal__body">
            <section className="prompt-edit-modal__section">
              <label className="prompt-edit-modal__label" htmlFor="pem-description">
                Description
              </label>
              <textarea
                id="pem-description"
                className="prompt-edit-modal__textarea"
                placeholder="Add context for recruiters (optional)"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                disabled={saving}
              />
            </section>

            <section className="prompt-edit-modal__section">
              <VisibilitySelectDropdown
                value={visibility}
                onChange={setVisibility}
                itemLabel="screening prompt"
                label="Privacy"
                disabled={saving}
              />
            </section>

            <section className="prompt-edit-modal__section">
              <span className="prompt-edit-modal__label">Video response</span>
              {showVideo ? (
                <div className="prompt-edit-modal__video-wrap">
                  <video
                    key={activeVideoSrc ?? 'no-video'}
                    className="prompt-edit-modal__video"
                    controls={!uploadBusy}
                    src={activeVideoSrc ?? undefined}
                    playsInline
                  >
                    <track kind="captions" />
                  </video>
                  {uploadBusy ? (
                    <div className="prompt-edit-modal__video-saving" aria-live="polite">
                      Saving video…
                    </div>
                  ) : null}
                </div>
              ) : (
                <p className="prompt-edit-modal__file-hint">
                  No video yet. Record or upload one below.
                </p>
              )}
              {videoFile ? (
                <p className="prompt-edit-modal__file-hint prompt-edit-modal__file-hint--new">
                  New video: {videoFile.name}
                </p>
              ) : null}
              <div className="prompt-edit-modal__actions">
                <button
                  type="button"
                  className="prompt-edit-modal__btn prompt-edit-modal__btn--secondary"
                  disabled={saving}
                  onClick={() => setRecordingOpen(true)}
                >
                  Record video
                </button>
                <button
                  type="button"
                  className="prompt-edit-modal__btn prompt-edit-modal__btn--secondary"
                  disabled={saving}
                  onClick={() => uploadInputRef.current?.click()}
                >
                  Upload video
                </button>
              </div>
              <input
                ref={uploadInputRef}
                className="visually-hidden"
                type="file"
                accept="video/*"
                aria-label="Upload video file"
                onChange={(e) => setVideoSelection(e.target.files?.[0] ?? null)}
              />
            </section>

            <section className="prompt-edit-modal__section">
              <span className="prompt-edit-modal__label">Supporting document</span>
              {docMeta && docUrl && !docFile ? (
                <p className="prompt-edit-modal__doc-current">
                  Current:{' '}
                  <a href={mediaUrl(docUrl)} target="_blank" rel="noopener noreferrer">
                    {docMeta.icon} {docMeta.name}
                  </a>
                  . Choose a new file to replace it.
                </p>
              ) : null}
              {docFile ? (
                <p className="prompt-edit-modal__file-hint prompt-edit-modal__file-hint--new">
                  New document: {docFile.name}
                </p>
              ) : null}
              <button
                type="button"
                className="prompt-edit-modal__btn prompt-edit-modal__btn--outline"
                disabled={saving}
                onClick={() => docInputRef.current?.click()}
              >
                {docFile ? 'Change document' : 'Choose document'}
              </button>
              <input
                ref={docInputRef}
                className="visually-hidden"
                type="file"
                accept=".pdf,.doc,.docx,image/*"
                aria-label="Upload supporting document"
                onChange={(e) => setDocFile(e.target.files?.[0] ?? null)}
              />
            </section>
          </div>

          <footer className="prompt-edit-modal__footer">
            {uploadBusy ? (
              <div className="prompt-edit-modal__footer-upload">
                <PromptVideoUploadBar progress={videoUpload!.progress} phase={videoUpload!.phase} />
              </div>
            ) : null}
            <div className="prompt-edit-modal__footer-toolbar">
              {showDelete && onDeleteRequest ? (
                <button
                  type="button"
                  className="prompt-edit-modal__btn prompt-edit-modal__btn--danger"
                  disabled={saving}
                  onClick={() => onDeleteRequest(prompt.id)}
                >
                  Delete
                </button>
              ) : (
                <span />
              )}
              <div className="prompt-edit-modal__footer-actions">
                <button
                  type="button"
                  className="prompt-edit-modal__btn prompt-edit-modal__btn--secondary"
                  disabled={saving}
                  onClick={handleClose}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="prompt-edit-modal__btn prompt-edit-modal__btn--primary"
                  disabled={saving}
                  onClick={() => void handleSave()}
                >
                  {saving ? 'Saving…' : 'Save changes'}
                </button>
              </div>
            </div>
          </footer>
        </div>
      </div>

      <PromptVideoRecordingModal
        show={recordingOpen}
        promptText={prompt.content}
        onHide={() => setRecordingOpen(false)}
        onVideoCaptured={(file) => {
          setVideoSelection(file);
          toast.success('Video ready. Save changes when you are done.');
        }}
      />
    </div>
  );
}

export default PromptEditModal;
